import React from "react";
import "./MetaAudienceForm.scss";
import { MenuItem, Select, FormControl, CircularProgress, Box, Typography } from "@mui/material";
import AudienceMultiSelect from "./AudienceMultiSelect";

const selectSx = {
  fontFamily: "Inter",
  fontSize: "12px",
  fontWeight: 400,
  color: "#373F41",
  height: "28px",
  minHeight: "28px",
  minWidth: "480px",
  borderRadius: "4px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C4C4C4",
    borderRadius: "4px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#61D5A9",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#61D5A9",
  },
  "& .MuiSelect-select": {
    paddingTop: "2px",
    paddingBottom: "2px",
  },
};

const errorSelectSx = {
  ...selectSx,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d32f2f",
    borderRadius: "4px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d32f2f",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d32f2f",
  },
};

const MetaAudienceForm = ({
  selectedIncludeAudiences = [],
  setSelectedIncludeAudiences,
  selectedExcludeAudiences = [],
  setSelectedExcludeAudiences,
  // Legacy single-select props (backward compat)
  selectedMetaAudience,
  setSelectedMetaAudience,
  metaAudienceOptions = [],
  isLoadingOptions = false,
  error = "",
}) => {
  // Determine if we're in multi-select mode
  const isMultiMode = !!setSelectedIncludeAudiences;

  if (isLoadingOptions) {
    return (
      <div className="meta-audience-form">
        <div className="meta-audience-dropdown-wrapper">
          <div className="meta-audience-loading">
            <CircularProgress size={16} sx={{ color: "#61D5A9" }} />
            <span className="filter-label">Loading audiences...</span>
          </div>
        </div>
      </div>
    );
  }

  // Legacy single-select mode
  if (!isMultiMode) {
    return (
      <div className="meta-audience-form">
        <div className="meta-audience-dropdown-wrapper">
          <div className="meta-audience-select-group">
            <span className="filter-label">AUDIENCE :</span>
            <FormControl size="small" error={!!error}>
              <Select
                value={selectedMetaAudience || ""}
                onChange={(e) => setSelectedMetaAudience(e.target.value)}
                displayEmpty
                error={!!error}
                sx={error ? errorSelectSx : selectSx}
              >
                <MenuItem value="" disabled sx={{ fontFamily: "Inter", fontSize: "12px" }}>
                  Select an audience
                </MenuItem>
                {metaAudienceOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ fontFamily: "Inter", fontSize: "12px" }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {error && (
                <span style={{ color: "#d32f2f", fontSize: "11px", marginTop: "4px", fontFamily: "Inter" }}>
                  {error}
                </span>
              )}
            </FormControl>
          </div>
        </div>
      </div>
    );
  }

  // Multi-select mode
  return (
    <div className="meta-audience-form">
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#373F41", fontFamily: "Inter" }}>
          AUDIENCES:
        </Typography>
        <Box sx={{ borderBottom: "1px solid #e0e0e0", marginBottom: "4px" }} />
        <Box sx={{ display: "flex", flexDirection: "row", gap: "24px", alignItems: "flex-start" }}>
          {/* Include Audiences */}
          <div className="meta-audience-dropdown-wrapper">
            <div className="meta-audience-select-group">
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#373F41", fontFamily: "Inter", marginBottom: "4px" }}>
                INCLUDE:
              </Typography>
              <AudienceMultiSelect
                value={selectedIncludeAudiences}
                onChange={setSelectedIncludeAudiences}
                options={metaAudienceOptions.filter((option) => !selectedExcludeAudiences.includes(String(option.value)))}
                placeholder="Select audiences to include"
                width={360}
                checkedColor="#61D5A9"
                error={error}
              />
            </div>
          </div>

          {/* Exclude Audiences */}
          <div className="meta-audience-dropdown-wrapper">
            <div className="meta-audience-select-group">
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#373F41", fontFamily: "Inter", marginBottom: "4px" }}>
                EXCLUDE:
              </Typography>
              <AudienceMultiSelect
                value={selectedExcludeAudiences}
                onChange={setSelectedExcludeAudiences}
                options={metaAudienceOptions.filter((option) => !selectedIncludeAudiences.includes(String(option.value)))}
                placeholder="Select audiences to exclude (optional)"
                width={360}
                checkedColor="#f44336"
              />
            </div>
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default MetaAudienceForm;
